/**
 * Sheet „Person bearbeiten" (`access-person-sheet`, Spec 25, Oberfläche D) —
 * öffnet aus `access-person-edit` in `routes/AccessUser.tsx`. Trägt Name,
 * E-Mail, Vollzugriff und die Zeile „Passwort setzen …" (öffnet
 * `AccessPersonPasswordSheet`).
 *
 * **D3 (Entscheidung Lukas, geht der Designabnahme-Auflage 3 vor):** Sperren/
 * Entsperren steht ab 768px **nicht** hier, sondern als eigener Knopf neben
 * „Bearbeiten" in `routes/AccessUser.tsx` — unter 768px dagegen als letzte
 * Zeile in diesem Sheet, außerhalb der Gruppen. Deshalb entscheidet dieses
 * Sheet selbst über `useIsDesktopViewport()`, ob die Zeile erscheint — die
 * Zusicherung „Sheet bleibt nach Sperren/Entsperren offen, Eingaben bleiben
 * stehen" (Auflage 3) gilt nur für diesen (mobilen) Fall; der Desktop-Knopf
 * lebt außerhalb jedes Sheets und schließt keins.
 *
 * `PATCH`-Rumpf trägt **nur geänderte Felder** (AK-1012) — nichts geändert →
 * „Speichern" deaktiviert, kein Aufruf mit leerem Rumpf.
 */

import { useState } from 'react'

import { ConfirmationStrip } from '@/components/ConfirmationStrip'
import { Sheet, SheetContent, SheetFooter, SheetHeader, SheetTitle } from '@/components/ui/sheet'
import { FIELD_CLASS, FormField } from '@/components/FormField'
import type { FieldError } from '@/components/FormField'
import { ApiError } from '@/lib/api'
import { useIsDesktopViewport } from '@/lib/viewport'
import { useUpdateUser } from '@/features/access/api'
import type { PersonHead, PersonUpdate } from '@/features/access/api'
import { AccessPersonPasswordSheet } from '@/features/access/AccessPersonPasswordSheet'
import { FullAccessCheckbox } from '@/features/access/FullAccessCheckbox'
import { PersonDisableAction } from '@/features/access/PersonDisableAction'

interface AccessPersonSheetProps {
  readonly open: boolean
  readonly onOpenChange: (open: boolean) => void
  readonly person: PersonHead
  /** `me.id` aus `GET /access/me` — für die eigene Person fehlt die
   * Sperren-Zeile ganz (Server sagt ohnehin 409 `self_lockout`). */
  readonly selfId: string
}

interface FormValues {
  readonly displayName: string
  readonly email: string
  readonly fullAccess: boolean
}

function toFormValues(person: PersonHead): FormValues {
  return {
    displayName: person.display_name ?? '',
    email: person.email,
    fullAccess: person.full_access,
  }
}

function toChangedFields(form: FormValues, person: PersonHead): PersonUpdate {
  const body: PersonUpdate = {}
  if (form.displayName !== (person.display_name ?? '')) body.display_name = form.displayName
  if (form.email !== person.email) body.email = form.email
  if (form.fullAccess !== person.full_access) body.full_access = form.fullAccess
  return body
}

export function AccessPersonSheet({ open, onOpenChange, person, selfId }: AccessPersonSheetProps) {
  const updateMutation = useUpdateUser()
  const isDesktop = useIsDesktopViewport()

  const [form, setForm] = useState<FormValues>(() => toFormValues(person))
  const [fieldError, setFieldError] = useState<FieldError | undefined>(undefined)
  const [generalError, setGeneralError] = useState<string | undefined>(undefined)
  const [isPasswordSheetOpen, setIsPasswordSheetOpen] = useState(false)
  const [passwordDoneMessage, setPasswordDoneMessage] = useState<string | null>(null)

  // Jedes Öffnen beginnt mit dem aktuellen Stand der Person, nicht mit dem
  // vom letzten Öffnen — dieselbe Technik wie `AccessInviteSheet`. Während
  // das Sheet offen bleibt (Sperren/Entsperren, Auflage 3), überschreibt ein
  // neu geladener `person`-Stand die Eingaben absichtlich **nicht**.
  const [wasOpen, setWasOpen] = useState(open)
  if (open !== wasOpen) {
    setWasOpen(open)
    if (open) {
      setForm(toFormValues(person))
      setFieldError(undefined)
      setGeneralError(undefined)
      setPasswordDoneMessage(null)
    }
  }

  const changed = toChangedFields(form, person)
  const canSave = Object.keys(changed).length > 0

  async function submit() {
    if (!canSave) return
    setFieldError(undefined)
    setGeneralError(undefined)
    try {
      await updateMutation.mutateAsync({ userId: person.id, body: changed })
      onOpenChange(false)
    } catch (error) {
      if (error instanceof ApiError && error.field !== null) {
        setFieldError({ field: error.field, message: error.message })
      } else if (error instanceof ApiError) {
        setGeneralError(error.message)
      } else {
        setGeneralError('Die Verbindung zum Server ist unterbrochen.')
      }
    }
  }

  const isDisabled = person.state.value === 'disabled'
  // Unter 768px liegt Sperren/Entsperren hier (D3); am Schreibtisch steht der
  // Knopf neben „Bearbeiten" in `routes/AccessUser.tsx`.
  const showDisableRowHere = !isDesktop && person.id !== selfId

  return (
    <>
      <Sheet open={open} onOpenChange={onOpenChange}>
        <SheetContent
          side="right"
          showCloseButton={false}
          className="data-[side=right]:!w-full data-[side=right]:sm:!max-w-none md:data-[side=right]:!w-[480px] md:data-[side=right]:!max-w-[480px]"
        >
          <div data-testid="access-person-sheet" className="flex h-full flex-col">
            <SheetHeader>
              <SheetTitle className="text-section font-(--font-weight-section) text-text">
                Person bearbeiten
              </SheetTitle>
            </SheetHeader>

            <div className="flex flex-1 flex-col gap-4 overflow-y-auto px-4">
              <FormField
                label="Name"
                testId="access-person-name"
                apiField="display_name"
                errorPrefix="access-person"
                required
                value={form.displayName}
                onChange={(value) => {
                  setForm((current) => ({ ...current, displayName: value }))
                }}
                error={fieldError}
              />

              <FormField
                label="E-Mail"
                testId="access-person-email"
                apiField="email"
                errorPrefix="access-person"
                type="email"
                inputMode="email"
                required
                value={form.email}
                onChange={(value) => {
                  setForm((current) => ({ ...current, email: value }))
                }}
                error={fieldError}
              />

              <FullAccessCheckbox
                testId="access-person-full-access"
                checked={form.fullAccess}
                onChange={(checked) => {
                  setForm((current) => ({ ...current, fullAccess: checked }))
                }}
                disabled={person.is_external}
              />

              <button
                type="button"
                data-testid="access-person-password"
                onClick={() => {
                  setIsPasswordSheetOpen(true)
                }}
                className={`text-text px-4 text-left ${FIELD_CLASS}`}
              >
                Passwort setzen …
              </button>

              {showDisableRowHere && (
                <PersonDisableAction personId={person.id} isDisabled={isDisabled} tapMin onError={setGeneralError} />
              )}
            </div>

            <SheetFooter>
              {generalError !== undefined && (
                <p data-testid="access-person-error" role="alert" className="text-label text-status-overdue">
                  {generalError}
                </p>
              )}
              <div className="flex flex-wrap justify-end gap-2">
                <button
                  type="button"
                  data-testid="access-person-cancel"
                  onClick={() => {
                    onOpenChange(false)
                  }}
                  className={`text-text px-4 ${FIELD_CLASS}`}
                >
                  Abbrechen
                </button>
                <button
                  type="button"
                  data-testid="access-person-save"
                  disabled={!canSave || updateMutation.isPending}
                  aria-busy={updateMutation.isPending}
                  onClick={() => void submit()}
                  className="bg-accent rounded-card text-body px-4 text-accent-on transition-opacity duration-(--duration-fast) ease-(--ease-out) disabled:opacity-60"
                >
                  Speichern
                </button>
              </div>
            </SheetFooter>
          </div>

          {/* Die Rückfrage für Sperren/Entsperren lebt in `PersonDisableAction`
           * selbst — deren `ConfirmationStrip` bleibt innerhalb von
           * `SheetContent` (wie `access-revoke-confirmation` in
           * `AccessSheet.tsx`), sonst markiert Radix' offenes Dialog alles
           * außerhalb seines Portals als `aria-hidden`, und kein Klick käme
           * durch. */}
          <ConfirmationStrip
            testId="access-person-password-done"
            message={passwordDoneMessage}
            onDismiss={() => {
              setPasswordDoneMessage(null)
            }}
          />
        </SheetContent>
      </Sheet>

      <AccessPersonPasswordSheet
        open={isPasswordSheetOpen}
        onOpenChange={setIsPasswordSheetOpen}
        userId={person.id}
        onSaved={() => {
          setPasswordDoneMessage('Passwort gesetzt.')
        }}
      />
    </>
  )
}
